var _excluded = ["children", "className", "amount", "suffix", "inline", "isInverse", "size", "color", "isSuffixColored", "strong", "label", "align", "noWrap", "isFixedDecimal", "utils"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import InverseContext from '../../utilities/inverse-context';
import StyledPrice from './price.styles';
import StyledPriceCurrency from './styles/price.currency.styles';
import StyledPriceWrapper from './styles/price.wrapper.styles';
import StyledPriceAlignment from './styles/price.alignment.styles';
import StyledPriceFractional from './styles/price.fractional.styles';
import StyledPriceHyphen from './styles/price.hyphen.styles';
import Type from '../type';
import Label from '../label';
var PriceProps = {
  /** Child elements to be rendered below the Price */
  children: PropTypes.node,
  /**
   * Add custom CSS classes to component.
   */
  className: PropTypes.string,
  /**
   * Change color of the price text to: 'navy', 'gray', 'green', 'coral'
   */
  color: PropTypes.oneOf(['navy', 'gray', 'green', 'coral']),
  /**
   * Changes the color of the prices suffix text to match the amounts color variant
   */
  isSuffixColored: PropTypes.bool,
  /**
   * Specifies the amount as either a single value or a range using an array.
   */
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.array, PropTypes.string]).isRequired,
  /**
   * Adds a sub string for example `/6Mo, /year`
   */
  suffix: PropTypes.node,
  /**
   * Choose a size (t-shirt sizing)
   */
  size: PropTypes.oneOf(['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl']),
  /**
   * Adds bold modifiers to currency and suffix
   */
  strong: PropTypes.bool,
  /**
   * All price text is the same size
   */
  inline: PropTypes.bool,
  /**
   * Control the Inverse styling of the Price text color
   */
  isInverse: PropTypes.oneOf(['auto', true, false]),
  /** Label that will be displayed with above the price */
  label: PropTypes.node,
  /** Price label, amount and suffix alignment */
  align: PropTypes.oneOf(['left', 'center', 'right']),
  /**
   * Set price content to no-wrap
   */
  noWrap: PropTypes.bool,
  /**
   * Show or hide .00 cents in pricing
   */
  isFixedDecimal: PropTypes.bool,
  /**
   * Add utility objects for styling
   */
  utils: PropTypes.object
};
var defaultProps = {
  children: null,
  className: '',
  color: null,
  isSuffixColored: false,
  suffix: '',
  inline: false,
  size: null,
  strong: true,
  isInverse: 'auto',
  label: '',
  align: 'left',
  noWrap: false,
  isFixedDecimal: false,
  utils: {}
};
var sizeFractionalText = function sizeFractionalText(size) {
  var sizes = {
    '8xl': '4xl',
    '7xl': '3xl',
    '6xl': '2xl',
    '5xl': 'l',
    '4xl': 'm',
    '3xl': 'm',
    '2xl': '2xs',
    xl: '2xs',
    lg: '2xs',
    md: '2xs',
    sm: '2xs',
    xs: '2xs',
    '2xs': '2xs'
  };
  return sizes[size] || 'm';
};
var sizeCurrencyText = function sizeCurrencyText(size) {
  var sizes = {
    '8xl': '4xl',
    '7xl': '3xl',
    '6xl': '2xl',
    '5xl': 'xl',
    '4xl': 'l',
    '3xl': 'l',
    '2xl': 's',
    xl: 's',
    lg: '2xs',
    md: '2xs',
    sm: '2xs',
    xs: '2xs',
    '2xs': '2xs'
  };
  return sizes[size] || 'l';
};
var sizeSuffixText = function sizeSuffixText(size) {
  var sizes = {
    '8xl': '8xl',
    '7xl': '7xl',
    '6xl': '6xl',
    '5xl': '5xl',
    '4xl': '4xl',
    '3xl': '3xl',
    '2xl': '2xl',
    xl: 'xl',
    lg: 'l',
    md: 'm',
    sm: 's',
    xs: 'xs',
    '2xs': '2xs'
  };
  return sizes[size] || '5xl';
};
var colorToVariant = function colorToVariant(color) {
  switch (color) {
    case 'coral':
      return 'community';
    case 'green':
      return 'success';
    case 'gray':
      return 'default';
    case 'navy':
      return 'dark';
    default:
      return 'default';
  }
};
var createLabel = function createLabel(label) {
  if (label) {
    return /*#__PURE__*/React.createElement("legend", null, /*#__PURE__*/React.createElement(Label, {
      text: label,
      required: true
    }));
  }
};
var dollarsCentsFormat = function dollarsCentsFormat(amount) {
  var parsedAmount = parseFloat(amount);
  if (parsedAmount > 0 && parsedAmount < 1) {
    return '¢';
  } else {
    return '$';
  }
};
var numeralFormat = '0,0[.]00[00]';

/**
 * @ignore @description
 * ## Price Component
 * Use the Price component to display amounts intended as a stand-alone component or used inside of form elements.
 *
 * @ignore @see Mesh: [Price Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/informational-price--default)
 *
 * @ignore @example
 * ```jsx
 * import { Price } from 'mesh-component-library';
 *
 * <Price {...props}>
 * {children}
 * </Price>
 * ```
 */

var Price = function Price(props) {
  var children = props.children,
    className = props.className,
    amount = props.amount,
    suffix = props.suffix,
    inline = props.inline,
    isInverse = props.isInverse,
    size = props.size,
    color = props.color,
    isSuffixColored = props.isSuffixColored,
    strong = props.strong,
    label = props.label,
    align = props.align,
    noWrap = props.noWrap,
    isFixedDecimal = props.isFixedDecimal,
    utils = props.utils,
    other = _objectWithoutProperties(props, _excluded);
  var useIsInverse = React.useContext(InverseContext);
  if (isInverse !== 'auto') {
    useIsInverse = isInverse;
  }
  var fontWeight = 'semibold';
  if (strong) {
    fontWeight = 'bold';
  }
  var renderAmount = function renderAmount(amountToRender) {
    var nonInlineAmount = amountToRender.toString();
    var amountFormatted = numeral(nonInlineAmount).format(numeralFormat);
    var amountIndex = amountFormatted.split(/(\.\d+)/);
    if (isFixedDecimal && amountIndex[1] === undefined) {
      amountIndex[1] = '.00';
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Type, {
      element: "span",
      variant: colorToVariant(color),
      weight: fontWeight,
      isInverse: isInverse,
      utils: {
        hrRight: 'xs'
      },
      size: sizeSuffixText(size)
    }, inline ? dollarsCentsFormat(amountFormatted) : /*#__PURE__*/React.createElement(StyledPriceCurrency, {
      size: size,
      suffix: suffix
    }, /*#__PURE__*/React.createElement(Type, {
      weight: "normal",
      size: sizeCurrencyText(size),
      variant: colorToVariant(color),
      isInverse: isInverse,
      element: "span"
    }, dollarsCentsFormat(amountFormatted))), /*#__PURE__*/React.createElement(Type, {
      size: size,
      variant: colorToVariant(color),
      isInverse: isInverse,
      element: "span"
    }, amountIndex[0], inline ? amountIndex[1] : /*#__PURE__*/React.createElement(StyledPriceFractional, {
      size: size
    }, /*#__PURE__*/React.createElement(Type, {
      size: sizeFractionalText(size),
      variant: colorToVariant(color),
      weight: fontWeight,
      isInverse: isInverse,
      element: "span"
    }, amountIndex[1])))));
  };
  var renderRange = function renderRange() {
    var firstRange = amount[0].toString();
    var secondRange = amount[1].toString();
    var firstRangeFormatted = numeral(firstRange).format(numeralFormat);
    var secondRangeFormatted = numeral(secondRange).format(numeralFormat);
    var firstRangeDecimal = firstRangeFormatted.split(/(\.\d+)/);
    var secondRangeDecimal = secondRangeFormatted.split(/(\.\d+)/);
    if (isFixedDecimal && firstRangeDecimal[1] === undefined && secondRangeDecimal[1] === undefined) {
      firstRangeDecimal[1] = '.00';
      secondRangeDecimal[1] = '.00';
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, renderAmount(firstRangeDecimal), /*#__PURE__*/React.createElement(StyledPriceHyphen, {
      size: size,
      inline: inline
    }, /*#__PURE__*/React.createElement(Type, {
      size: sizeSuffixText(size),
      variant: colorToVariant(color),
      weight: fontWeight,
      isInverse: isInverse,
      element: "span",
      utils: {
        hrRight: 'xs'
      }
    }, "-")), renderAmount(secondRangeDecimal));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledPriceAlignment, {
    align: align
  }, createLabel(label), /*#__PURE__*/React.createElement(StyledPrice, _extends({
    className: className,
    size: size,
    isInverse: useIsInverse,
    inline: inline,
    color: color,
    utils: utils
  }, other), /*#__PURE__*/React.createElement(StyledPriceWrapper, {
    align: align,
    noWrap: noWrap
  }, amount && _typeof(amount) === 'object' ? renderRange() : renderAmount(amount), suffix && /*#__PURE__*/React.createElement(Type, {
    size: sizeSuffixText(size),
    variant: isSuffixColored ? colorToVariant(color) : colorToVariant(),
    element: "span",
    isInverse: isInverse,
    weight: "thin"
  }, suffix))), /*#__PURE__*/React.createElement(Type, {
    element: "p"
  }, children)));
};
Price.propTypes = PriceProps;
Price.defaultProps = defaultProps;
export default Price;